import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type { ScreenshotAuditItem, ScreenshotAuditQueue, ScreenshotVerdict } from '@/api/types';

const AUDIT_KEY = ['admin', 'screenshot-audit'] as const;

export interface AuditFilters {
  state?: 'pending' | ScreenshotVerdict;
  team?: string;
  /** Comma list of teams to leave out (the main view: Influencer, Vyral). */
  excludeTeam?: string;
  limit?: number;
  /** Only invites still in play: not expired, rejected or hidden. Counts follow. */
  live?: boolean;
  /** By when the invite was logged. Without it, proofs to review come oldest first. */
  order?: 'newest' | 'oldest';
}

/**
 * A page of screenshots with presigned URLs already resolved server-side.
 *
 * `refetchOnWindowFocus` matters here specifically: the URLs are presigned and
 * expire. A queue left open over lunch would otherwise render broken images,
 * and the reviewer would read that as "the screenshots are missing".
 */
export function useScreenshotAuditQueue(filters: AuditFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [...AUDIT_KEY, filters],
    queryFn: () =>
      api.get<ScreenshotAuditQueue>('/admin/screenshot-audit', {
        state: filters.state,
        team: filters.team || undefined,
        excludeTeam: filters.team ? undefined : filters.excludeTeam || undefined,
        live: filters.live ? 'true' : undefined,
        order: filters.order,
        // The API caps a page at 50.
        limit: filters.limit !== undefined ? Math.min(50, Math.max(1, filters.limit)) : undefined,
      }),
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useReviewScreenshot() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      invitePublicId: string;
      verdict: ScreenshotVerdict;
      note?: string | null;
    }) =>
      api.post<ScreenshotAuditItem>(
        `/admin/screenshot-audit/${encodeURIComponent(input.invitePublicId)}/review`,
        { verdict: input.verdict, note: input.note ?? null },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AUDIT_KEY });
      // The verdict also shows on the invite (Invites table, sheet, Proof column).
      qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
  });
}
